<template>
	<!-- poster-detail--above: determines slightly the layout to have bigger fonts and dividers -->
	<div class="poster-detail" :class="{ 'poster-detail--above': aboveTheFold, 'poster-detail--below': !aboveTheFold }">
		<div class="poster-detail-infos" v-for="key in renderedKeys" :key="key">
			<h3 class="poster-detail-infos__subheading">{{ $t(ITEMS[key].subheadingTranslatable) }}</h3>

			<div class="poster-detail-infos__value">
				<!-- directors -->
				<template v-if="key == 'directors'">
					<div class="poster-detail-infos__value directors" :class="{ 'show-all': !trimDirectors }">
						<span v-for="(credit, index) in directors" :key="credit.personId + '-' + index">
							<template v-if="index !== 0">, </template>
							<TitleCreditName :credit="credit" />
						</span>
					</div>

					<label v-if="directors.length > 5" class="show-more-directors">
						<input class="sr-only" type="checkbox" v-model="trimDirectors" />
						{{ trimDirectors ? $t('WEBAPP_READ_MORE') : $t('WEBAPP_READ_LESS') }}
					</label>
				</template>

				<!-- scores -->
				<template v-if="key == 'scores'">
					<div class="poster-detail-infos__value">
						<TitleScoreList
							:scores="scoring"
							:externalIds="titleContent.externalIds"
							:show-votes="true"
							:imdb-only="!isReleased"
							:additionalContexts="additionalContexts"
							:jwVotes="jwVotes"
							:isPosterDetail="aboveTheFold"
						/>
					</div>
				</template>

				<!-- genres -->
				<template v-if="key == 'genres'">
					<div class="poster-detail-infos__value">
						<span>{{ genres.join(', ') }}</span>

						<template v-if="showSubgenres">
							<!-- we assume that subgenres ALWAYS have genres -->
							<span>,&nbsp;</span>
							<span>
								<router-link
									v-for="(subgenre, i) in subgenres"
									class="detail-infos__value detail-infos__link"
									:to="{ path: subgenre.url.fullPath }"
									:key="subgenre.name"
									@click="trackSubgenresClick(subgenre.name)"
									>{{ subgenre.name }}<span v-if="i !== subgenres.length - 1">,&nbsp;</span>
								</router-link>
							</span>
						</template>
					</div>
				</template>

				<!-- runtime -->
				<template v-if="key == 'runtime'">
					<div class="poster-detail-infos__value">{{ runtime }}</div>
				</template>

				<!-- ageRating -->
				<template v-if="key == 'ageRating'">
					<div class="poster-detail-infos__value">{{ ageCertifications }}</div>
				</template>

				<!-- productionCountries -->
				<template v-if="key == 'productionCountries'">
					<div class="poster-detail-infos__value">{{ productionCountries }}</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { CreditRole } from '@/@types/graphql-types';
import TitleCreditName from '@/components/details/TitleCreditName.vue';
import TitleScoreList from '@/components/TitleScoreList.vue';
import { SnowplowContext, SnowplowTitleContext } from '@/helpers/tracking/providers';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed, ref } from 'vue';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { formatRuntime } from '@/helpers/title-helper';
import { activeSubgenreCountries } from '@/constants/subgenres';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

const props = withDefaults(
	defineProps<{
		title: TitleDetail;
		hasStreamingChart: boolean;
		additionalContexts?: SnowplowContext[];
		itemLimit?: number; // undefined = unlimited
		itemStartAt?: number;
		aboveTheFold?: boolean; // below the fold gets smaller font-size and dividers
	}>(),
	{
		additionalContexts: () => [],
		hasStreamingChart: false,
		itemStartAt: 0,
		aboveTheFold: false,
	}
);

const { urlMetadata } = storeToRefs(useRoutingStore());
const { isPremium } = storeToRefs(useUserStore());
const { country } = storeToRefs(useLanguageStore());
const { t } = useI18n();

const subgenreToGenreDictionary = {
	'Mystery Thriller': 'Mystery & Thriller',
	'Action Adventure': 'Action & Adventure',
};

type RedundantSubgenre = keyof typeof subgenreToGenreDictionary;
type Subgenre = TitleDetail['content']['subgenres'][number]['content'] & {
	url: { fullPath: string };
};

const titleContent = computed(() => props.title.content);

const scoring = computed(() => titleContent.value.scoring);

const htmlContent = computed(() => urlMetadata.value?.htmlContentJson ?? {});

const isReleased = computed(() => titleContent.value.isReleased);

const jwVotes = computed(() => props.title.content.interactions?.votesNumber ?? null);

const directors = computed(() => {
	const credits =
		props.title.__typename === 'Season' ? props.title.show.content.directors : props.title.content.directors;

	return credits.filter(credit => credit.role === CreditRole.Director);
});
const trimDirectors = ref(directors.value.length > 5);

const runtime = computed(() => {
	const runtimeValue = titleContent.value.runtime!;
	const { translationKey, minutes, hours } = formatRuntime(runtimeValue);

	return t(translationKey, { minutes, hours }) as string;
});

const subgenres = computed(() => {
	return props.title.content.subgenres.map(({ content }) => content).filter(el => el.url) as Subgenre[];
});

const genres = computed(() => {
	const genresToRemove = new Set();
	subgenres.value.forEach(({ name }) => {
		if (subgenreToGenreDictionary[name as RedundantSubgenre] != null) {
			genresToRemove.add(subgenreToGenreDictionary[name as RedundantSubgenre]);
		}
	});

	return props.title.content.genres
		.map(genre => genre.translation)
		.filter(translation => !!translation && !genresToRemove.has(translation));
});

const showSubgenres = computed(() => {
	return activeSubgenreCountries.includes(country.value) && subgenres.value.length > 0;
});

const ageCertifications = computed(() => props.title.__typename !== 'Season' && props.title.content.ageCertification);

const productionCountries = computed(() => {
	const titleContent = props.title.__typename === 'Season' ? props.title.show.content : props.title.content;

	return titleContent.productionCountries?.map(country => t(`WEBAPP_COUNTRY_${country}`)).join(', ');
});

// the order is kept when using non-numeric keys.
const ITEMS = computed(() => ({
	// streamcharts
	directors: {
		subheadingTranslatable: 'WEBAPP_PERSONS_DIRECTOR',
		hasContent: directors.value.length > 0,
	},
	scores: {
		subheadingTranslatable: 'WEBAPP_RATING',
		hasContent:
			!htmlContent.value.hide_all_scorings &&
			(scoring.value.imdbScore || scoring.value.jwRating) &&
			isReleased.value,
	},
	genres: {
		subheadingTranslatable: 'WEBAPP_GENRES',
		hasContent: genres.value.length > 0,
	},
	runtime: {
		subheadingTranslatable: 'WEBAPP_RUNTIME',
		hasContent: titleContent.value.runtime,
	},
	ageRating: {
		subheadingTranslatable: 'WEBAPP_AGERATING',
		hasContent: props.title.__typename !== 'Season' && props.title.content.ageCertification,
	},
	productionCountries: {
		subheadingTranslatable: 'WEBAPP_TITLE_DETAIL_PROD_COUNTRIES',
		hasContent: isPremium || productionCountries,
	},
	// (directors)
}));
const renderedKeys = computed<(keyof typeof ITEMS.value)[]>(() => {
	const itemStartAt = props.itemStartAt - (props.hasStreamingChart ? 2 : 0);
	return (
		(Object.keys(ITEMS.value) as (keyof typeof ITEMS.value)[])
			// filter out items that have no content (e.g. no directors, no scores, etc)
			.filter(key => ITEMS.value[key].hasContent)
			// start picking up items at `itemStartAt`, considering if title has streaming charts
			.filter((_key, index) => index >= itemStartAt)
			// limit to only `itemLimit`
			.filter((_key, index) => !props.itemLimit || index < props.itemLimit)
	);
});

function trackSubgenresClick(subgenre: string) {
	const seasonNumber = props.title.__typename === 'Season' ? props.title.content.seasonNumber : null;
	const contexts = (props.additionalContexts || []).concat([
		new SnowplowTitleContext(props.title.objectId, props.title.objectType, seasonNumber),
	]);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'subgenre_clicked',
			label: subgenre,
		},
		contexts
	);
}
</script>

<style lang="scss" scoped>
@import '~@/styles/sports.scss';
@import '~@/styles/variables.scss';

.poster-detail {
	display: flex;
	flex-direction: column;
	justify-content: space-evenly;
	padding-bottom: 16px;
	gap: 16px;

	&--below {
		gap: 0;
	}

	&-infos {
		display: flex;
		flex-direction: column;
		gap: 10px;
		font-weight: 400;

		.poster-detail--below & {
			margin-top: 24px;
			padding-top: 24px;
			border-top: 1px solid #1c252f;
		}

		&__subheading {
			font-weight: 700;
			text-transform: uppercase;
			font-size: 16px;
			color: $color-text-dark;
			margin: 0;
			margin-block: 16px 8px;
			margin: 0;
			margin-block-start: 0;

			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		&__value {
			color: $jw-color-lightgray--dark;

			.poster-detail--above & {
				font-size: 16px;
			}

			a:hover {
				cursor: pointer;
			}

			// take out link color in director link
			.poster-detail--above & {
				:deep(.title-credit-name) {
					color: $jw-color-lightgray--dark;
				}
			}
		}
	}
}

.directors {
	position: relative;
	height: 2.7lh;
	line-clamp: 3;
	-webkit-line-clamp: 3;
	overflow: hidden;
	text-overflow: ellipsis;

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		height: 100%;
		background: linear-gradient(to top, var(--jw-background-color) 0, transparent 20px, transparent 100%);
		z-index: 100;
		pointer-events: none;
	}

	&.show-all {
		height: auto;
		line-clamp: unset;
		-webkit-line-clamp: unset;

		&::after {
			background: none;
		}
	}

	& + .show-more-directors {
		display: block;
		margin-top: 8px;
		color: var(--link-link-medium, #78a6b8);
		cursor: pointer;

		.sr-only {
			clip: rect(0 0 0 0);
			clip-path: inset(50%);
			height: 1px;
			overflow: hidden;
			position: absolute;
			white-space: nowrap;
			width: 1px;
		}
	}
}
</style>
